import {
  AfterViewInit,
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {IClientsTile, IClientsTileApi} from '../conspicuity-detail/clients-tile.interface';
import {CellClassParams, CellClickedEvent, ColDef, GridApi, GridOptions, ITooltipParams} from 'ag-grid-community';
import {DatePipe, DecimalPipe} from '@angular/common';
import {Column} from 'ag-grid-community/dist/lib/entities/column';
import {CustomHeaderComponent} from './custom-header/custom-header.component';
import {AgGridModule} from 'ag-grid-angular';
import {debounceTime, Subscription} from "rxjs";
import {MatButton} from '@angular/material/button';
import {AG_GRID_GERMAN_LOCALE} from "frontier";

type TField =
  'firstName' |
  'lastName' |
  'birthday' |
  'payer' |
  'identifiernumber' |
  'numberOfCarts' |
  'streetAndHouseNumber' |
  'city' |
  'score' |
  'postcode'

interface Row {
  field: string;

  [clientRowId: string]: any
}

interface IFieldMapping {
  fieldName: TField;
  displayName: string;
}

interface IClientData extends IClientsTile {
  signature: string;
}

@Component({
  selector: 'app-clients-comparator-table',
  templateUrl: './clients-comparator-table.component.html',
  styleUrl: './clients-comparator-table.component.scss',
  standalone: true,
  imports: [AgGridModule, MatButton],
})
export class ClientsComparatorTableComponent implements OnInit, AfterViewInit, OnDestroy {
  subs: Subscription = new Subscription();

  @Output() takeData: EventEmitter<IClientsTileApi> = new EventEmitter<IClientsTileApi>();
  @Output() getAnotherClient: EventEmitter<number> = new EventEmitter<number>();
  @Output() onSeparateClientReference: EventEmitter<null> = new EventEmitter();
  @Output() initialized: EventEmitter<null> = new EventEmitter();

  datePipe: DatePipe = inject(DatePipe);
  numberPipe: DecimalPipe = inject(DecimalPipe);
  selectedColumn: Column;
  gridOptions: GridOptions = {
    localeText: AG_GRID_GERMAN_LOCALE,
    rowHeight: 30,
    headerHeight: 45,
    tooltipShowDelay: 500,
    defaultColDef: {
      resizable: true,
      onCellClicked: (params: CellClickedEvent) => {
        this.selectedColumn = params.column;
      },
      suppressMovable: true,
      cellClass: (params: CellClassParams): string => {
        if (this.selectedColumn?.getColId() === params?.column?.getColId()) {
          return 'selected-column';
        }
        return '';
      },
      headerComponent: CustomHeaderComponent,
    },
  }

  rowData: Row[];
  colDefs: ColDef[] = [];
  context: {
    selectedHeader: Column,
    onHeaderClick: (column: Column) => void,
    onHeaderCtrlClick: (column: Column) => void;
  } = {
    selectedHeader: null,
    onHeaderClick: (column: Column) => {
      console.log(column)
      this.context.selectedHeader = column;
      this.gridApi?.refreshHeader();
    },
    onHeaderCtrlClick: (column: Column) => {
      if (column.getInstanceId() === this.context.selectedHeader?.getInstanceId()) {
        this.context.selectedHeader = null;
      } else {
        this.context.selectedHeader = column;
      }
      this.gridApi?.refreshHeader();
    },
  };
  gridApi: GridApi;

  private mappedClients: IClientData[];
  private fields: IFieldMapping[] = [
    {fieldName: 'score', displayName: 'Übereinstimmung'},
    {fieldName: 'firstName', displayName: 'Vorname'},
    {fieldName: 'lastName', displayName: 'Nachname'},
    {fieldName: 'birthday', displayName: 'Geburtstag'},
    {fieldName: 'payer', displayName: 'Krankenkasse'},
    {fieldName: 'identifiernumber', displayName: 'Versichertennummer'},
    {fieldName: 'numberOfCarts', displayName: '# Fahrten / -> Abgerechnet'},
    {fieldName: 'streetAndHouseNumber', displayName: 'Straße & Hausnummer'},
    {fieldName: 'postcode', displayName: 'Postleitzahl'},
    {fieldName: 'city', displayName: 'Ort'},
  ];
  private clientsApiData: IClientsTileApi[];
  columnResize$: EventEmitter<void> = new EventEmitter();

  @Input() set clients(clients: IClientsTileApi[]) {
    if (clients == null) return;

    this.clientsApiData = clients;

    this.mappedClients = clients.map((clientData: IClientsTileApi): IClientData => {
      return {
        firstName: clientData.client.firstname,
        lastName: clientData.client.lastname,
        birthday: this.datePipe.transform(clientData?.client.birthday),
        payer: clientData.client.payerreadonly,
        identifiernumber: clientData.client.identifiernumber,
        numberOfCarts: clientData.client.cartcountertotal + ' / ' + clientData.client.cartcounter,
        city: clientData.client.houseaddress?.city,
        streetAndHouseNumber: clientData.client.houseaddress?.street,
        postcode: clientData.client.houseaddress?.postcode,
        signature: clientData.client.signature,
        score: this.numberPipe.transform(clientData.score * 100) + '%',
      }
    });

    this.colDefs = [
      {
        headerName: '',
        field: 'field',
        pinned: 'left',
        cellClass: 'attributes',
        initialWidth: 205,
      },
      ...this.mappedClients.map((c: IClientData) => {
        return this.mapClientToCol(c)
      })
    ];

    // Base comparator (selected patient)
    this.colDefs[1].cellClass = 'original';
    this.colDefs[1].headerClass = 'original';
    this.colDefs[1].headerName = ' (Vergleichsbasis) \n ' + this.colDefs[1].headerName;
    this.colDefs[1].pinned = 'left';

    const rows: Row[] = [];
    this.fields.forEach((field: IFieldMapping) => {
      const row: Row = {
        field: field.displayName
      };
      this.colDefs.slice(1).forEach((colDef: ColDef) => {
        const client: IClientData = this.mappedClients.find((mpc: IClientData): boolean => mpc.signature === colDef.field);
        row[colDef.field] = client[field.fieldName as TField];
      })
      rows.push(row);
    })

    this.rowData = rows;
    console.log(this.rowData);
  };

  ngOnInit(): void {
    this.subs.add(
      this.columnResize$.pipe(
        (debounceTime(300)),
      ).subscribe(() => this.recalculateTooltipThreshold()),
    );
  }

  ngAfterViewInit(): void {
    this.initialized.emit();
    setTimeout(() => this.gridApi?.sizeColumnsToFit(), 50);
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private mapClientToCol(c: IClientData): ColDef {
    return {
      headerName: c.firstName + ' ' + c.lastName,
      colId: c.signature,
      field: c.signature,
      editable: false,
      tooltipValueGetter: (params: ITooltipParams): string => {
        // Calculate estimated cell width based on column width
        const columnWidth: number = params?.column?.getActualWidth(); // Get the width of the column
        if (!columnWidth) return '';

        const threshold: number = (columnWidth - 34) / 12; // Set a threshold relative to the column width
        return params.value?.length > threshold ? params.value : ''
      }
    } as ColDef;
  }

  onTakeData(): void {
    this.takeData.emit(this.clientsApiData.find(c => c.client.signature === this.context.selectedHeader.getColId()));
  }

  getMoreClients(): void {
    this.getAnotherClient.emit(this.mappedClients.length);
  }

  recalculateTooltipThreshold(): void {
    // Loop through each column
    this.colDefs = this.colDefs.map((column: ColDef) => {
      return {
        ...column,
        tooltipValueGetter: (params: ITooltipParams): string => {
          // Calculate estimated cell width based on column width
          const columnWidth: number = params?.column?.getActualWidth(); // Get the width of the column
          if (!columnWidth) return '';

          const threshold: number = (columnWidth - 34) / 12; // Set a threshold relative to the column width
          return params.value?.length > threshold ? params.value : ''
        },
      };
    });
  }
}
